/**
 * leadStatuses — single source of truth for the CRM pipeline stages.
 *
 * PR #9 expanded the pipeline from 7 → 13 stages, additively. Existing
 * statuses (new, contacted, meeting_scheduled, dossier, in_followup,
 * reserved, lost) are preserved with their Spanish labels updated where
 * the new pipeline gives them a clearer name. Legacy aliases (meeting →
 * meeting_scheduled, signed → reserved) keep resolving.
 *
 * Used by LeadDetailPage, MarketingDashboard kanban, LeadSidePanel,
 * LeadCard, and any other client that reads `lead.status`.
 */

export const LEAD_STATUSES = [
  { key: "new",                label: "Nuevo lead",            dotColor: "#94a3b8" }, // slate-400
  { key: "contacted",          label: "Contactado",            dotColor: "#3b82f6" }, // blue-500
  { key: "meeting_scheduled",  label: "Call agendada",         dotColor: "#06b6d4" }, // cyan-500
  { key: "dossier_pending",    label: "Dossier pendiente",     dotColor: "#a855f7" }, // purple-500
  { key: "dossier",            label: "Dossier enviado",       dotColor: "#8b5cf6" }, // violet-500
  { key: "call_done",          label: "Call realizada",        dotColor: "#0ea5e9" }, // sky-500
  { key: "in_followup",        label: "Interés alto",          dotColor: "#f59e0b" }, // amber-500
  { key: "villa_on_hold",      label: "Villa en hold",         dotColor: "#fb923c" }, // orange-400
  { key: "reservation_sent",   label: "Reserva enviada",       dotColor: "#22c55e" }, // green-500
  { key: "reserved",           label: "Reserva pagada",        dotColor: "#10b981" }, // emerald-500
  { key: "contract_signed",    label: "Contrato firmado",      dotColor: "#059669" }, // emerald-600
  { key: "closed_won",         label: "Cerrado ganado",        dotColor: "#047857" }, // emerald-700
  { key: "lost",               label: "Cerrado perdido",       dotColor: "#78716c" }, // stone-500
];

export const LEAD_STATUS_MAP = Object.fromEntries(
  LEAD_STATUSES.map(s => [s.key, s])
);

// Legacy → canonical mapping (matches backend _normalize_lead_status).
// `meeting` and `signed` are pre-CRM-MVP relics. The 7 statuses present
// before PR #9 are all canonical, so no remap is needed for them.
const LEGACY_MAP = { meeting: "meeting_scheduled", signed: "reserved" };

export function normalizeStatus(s) {
  return LEGACY_MAP[s] || s || "new";
}

export function statusLabel(s) {
  const k = normalizeStatus(s);
  return (LEAD_STATUS_MAP[k] && LEAD_STATUS_MAP[k].label) || k;
}

// --- Signals (keep in sync with backend compute_lead_signals) ---
export const SIGNAL_META = {
  missing_next_action: { label: "Sin próxima acción", tone: "amber" },
  overdue_action:      { label: "Acción vencida",     tone: "red"   },
  unassigned:          { label: "Sin responsable",    tone: "amber" },
  stale:               { label: "Sin actividad 14+ días", tone: "gray" },
};

export function hasAnySignal(lead) {
  return Array.isArray(lead.signals) && lead.signals.length > 0;
}
